import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  Tooltip,
  useModal,
} from "@homebound/beam";
import { useMemo } from "react";
import { Price } from "src/components";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { CheckoutOptionDiffModal } from "src/routes/libraries/product-offerings/checkout-pricing-trace/CheckoutOptionDiffModal";
import { CheckoutPricingTraceModal } from "src/routes/libraries/product-offerings/checkout-pricing-trace/CheckoutPricingTraceModal";
import { fail, queryResult } from "src/utils";
import {
  CheckoutPricingTrace_OptionGroupFragment,
  CheckoutPricingTracePageOverview_TraceFragment,
  useCheckoutPricingTracePage_OverviewQuery,
} from "./CheckoutPricingTracePage.generated";

/** Shows the per-option group included/price drift of a `CheckoutConfig`, linked by the trade costs that drove the diff. */
export function CheckoutPricingTracePage() {
  const { configId } = useRouteParams<{ configId: string }>();
  const query = useCheckoutPricingTracePage_OverviewQuery({ variables: { ccfgId: configId } });
  return (
    <>
      <PageHeader title="Checkout Pricing Trace" />
      {queryResult(query, ({ checkoutPricingTrace }) => (
        <CheckoutPricingTracePageView trace={checkoutPricingTrace} />
      ))}
    </>
  );
}

type CheckoutPricingTracePageViewProps = { trace: CheckoutPricingTracePageOverview_TraceFragment };

function CheckoutPricingTracePageView({ trace }: CheckoutPricingTracePageViewProps) {
  const columns = useMemo(
    () =>
      createColumns(
        trace.checkoutConfig?.productOffering?.id ?? fail("Expected a checkoutConfig.productOffering"),
        trace.checkoutConfig?.aggregateVersion?.id ?? fail("Expected a checkoutConfig.aggregateVersion"),
      ),
    [trace],
  );
  const rows = useMemo(() => createRows(trace), [trace]);
  return <GridTable columns={columns} rows={rows} />;
}

type HeaderRow = { kind: "header"; data: CheckoutPricingTracePageOverview_TraceFragment };
type GroupRow = {
  kind: "group";
  id: string;
  data: CheckoutPricingTrace_OptionGroupFragment;
  /** The inferred full config RPO ids _before_ this group's included changes were applied. */
  includedConfigBefore: string[];
  /** The inferred full config RPO ids _after_ this group's included changes were applied. */
  includedConfigAfter: string[];
  /** The inferred full config RPO ids _before_ this group's selected changes were applied. */
  selectedConfigBefore: string[];
  /** The inferred full config RPO ids _after_ this group's selected changes were applied. */
  selectedConfigAfter: string[];
};
type Row = HeaderRow | GroupRow;

function createColumns(pofId: string, rpavId: string): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Group",
      group: (g) => {
        const path = g.group.location?.displayLocationPath;
        return (
          <Tooltip title={[g.group.id, g.group.step?.name, g.group.substepNumber].compact().join(" ")}>
            <div css={Css.df.fdc.$}>
              <div css={Css.sm.$}>{g.group.name}</div>
              {path && <div>{path}</div>}
            </div>
          </Tooltip>
        );
      },
    }),
    column<Row>({
      header: (data) => (
        <div css={Css.df.fdc.$}>
          <div>Included Config</div>
          <Price valueInCents={data.includedPriceInCents} />
        </div>
      ),
      group: (g, { row }) => {
        return (
          <GroupCell
            pofId={pofId}
            rpavId={rpavId}
            groupId={g.group.id}
            traceOption={g.included}
            optionGroup={g}
            configBefore={row.includedConfigBefore}
            configAfter={row.includedConfigAfter}
          />
        );
      },
    }),
    column<Row>({
      header: (data) => (
        <div css={Css.df.fdc.$}>
          <div>Selected Config</div>
          <Price valueInCents={data.selectedPriceInCents} />
        </div>
      ),
      group: (g, { row }) => {
        return (
          <GroupCell
            pofId={pofId}
            rpavId={rpavId}
            groupId={g.group.id}
            traceOption={g.selected}
            optionGroup={g}
            configBefore={row.selectedConfigBefore}
            configAfter={row.selectedConfigAfter}
          />
        );
      },
    }),
    numericColumn<Row>({
      header: "Credit",
      group: (g) => <Price valueInCents={g.availableCreditInCents} />,
      w: "100px",
    }),
    column<Row>({
      header: "Price",
      group: (g) => {
        return !g.included && !g.selected ? (
          "N/A"
        ) : g.priceChangeInCents === 0 ? (
          "Included"
        ) : (
          <Price valueInCents={g.priceChangeInCents} displayDirection={true} />
        );
      },
    }),
  ];
}

function createRows(trace: CheckoutPricingTracePageOverview_TraceFragment): GridDataRow<Row>[] {
  // Track the running full config (rpogId -> rpoId) for included and selected
  const includedConfig = new Map<string, string>();
  const selectedConfig = new Map<string, string>();
  return [
    { id: "header", kind: "header" as const, data: trace },
    // We don't sort the option groups, b/c the drift calc is fundamentally sequential, i.e. each
    // RPOG price/drift depends on the RPOG before it.
    ...trace.optionGroups.map((og) => {
      const includedBefore = [...includedConfig.values()];
      const selectedBefore = [...selectedConfig.values()];
      applyChanged(includedConfig, og.group.id, og.included);
      applyChanged(selectedConfig, og.group.id, og.selected);
      return {
        id: og.group.id,
        kind: "group" as const,
        data: og,
        includedConfigBefore: includedBefore,
        includedConfigAfter: [...includedConfig.values()],
        selectedConfigBefore: selectedBefore,
        selectedConfigAfter: [...selectedConfig.values()],
      };
    }),
  ];
}

/** Keeps our "current full config" up-to-date as we scan through changed entries,
 * including downstream option groups that were affected. */
function applyChanged(
  config: Map<string, string>,
  rpogId: string,
  traceOption: CheckoutPricingTrace_OptionGroupFragment["included"],
): void {
  if (!traceOption) return;
  // Set the primary option for this group
  if (traceOption.option) {
    config.set(rpogId, traceOption.option.id);
  } else {
    config.delete(rpogId);
  }
  // Also apply downstream changes — the changed array can reference other groups
  for (const entry of traceOption.changed) {
    if (entry.option) {
      config.set(entry.group.id, entry.option.id);
    } else if (entry.previousOption) {
      // Option was removed from this downstream group
      config.delete(entry.group.id);
    }
  }
}

/** The `included` or `selected` trace option for a given group. */
type TraceOption = CheckoutPricingTrace_OptionGroupFragment["included"];

function GroupCell(props: {
  pofId: string;
  rpavId: string;
  groupId: string;
  traceOption: TraceOption;
  optionGroup: CheckoutPricingTrace_OptionGroupFragment;
  configBefore: string[];
  configAfter: string[];
}) {
  const { pofId, rpavId, traceOption, optionGroup, configBefore, configAfter } = props;
  const { openModal } = useModal();

  const changed = traceOption?.changed ?? [];

  function changeLabel() {
    if (traceOption && changed.length === 0) {
      return "(included)";
    } else if (changed.length > 0) {
      return "(changed)";
    }
    return "";
  }

  return (
    <div css={Css.df.fdc.gap1.$}>
      {traceOption?.option?.name} {changeLabel()}
      <div css={Css.df.gap2.aic.$}>
        <Tooltip title={"Total price"}>
          <Price valueInCents={traceOption?.totalPriceInCents} />
        </Tooltip>
        {changed.length > 0 && (
          <Button
            variant="text"
            label="options"
            onClick={() => {
              openModal({
                content: <CheckoutOptionDiffModal entries={changed} optionGroup={optionGroup} />,
                size: "xxl",
              });
            }}
          />
        )}
        {changed.length > 0 && (
          <Button
            variant="text"
            label="scope"
            onClick={() => {
              openModal({
                content: (
                  <CheckoutPricingTraceModal
                    pofId={pofId}
                    rpavId={rpavId}
                    oldRpoIds={configBefore}
                    newRpoIds={configAfter}
                  />
                ),
                size: { width: "xxl", height: 700 },
              });
            }}
          />
        )}
      </div>
    </div>
  );
}
